import React from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle, CheckCircle, XCircle, X } from 'lucide-react';
import { Button } from './button';

/**
 * ConfirmModal - Modale di conferma personalizzata e coerente con il design del sito
 * 
 * Props:
 * - isOpen: boolean - Mostra/nasconde il modale
 * - onClose: function - Callback quando si chiude senza confermare
 * - onConfirm: function - Callback quando si conferma l'azione
 * - title: string - Titolo del modale
 * - message: string - Messaggio descrittivo
 * - details: string (optional) - Dettagli aggiuntivi (es. nome item da eliminare)
 * - confirmText: string - Testo del bottone di conferma (default: "Conferma")
 * - cancelText: string - Testo del bottone di annullamento (default: "Annulla")
 * - variant: 'danger' | 'warning' | 'info' | 'success' - Stile del modale
 * - icon: React component (optional) - Icona personalizzata
 */

const variants = {
  danger: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    confirmBtnClass: 'bg-red-600 hover:bg-red-700 text-white',
    Icon: AlertTriangle,  // Changed from Trash2 to AlertTriangle for consistency
    showIrreversibleWarning: true  // Show irreversible warning for delete actions
  },
  warning: {
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    confirmBtnClass: 'bg-amber-600 hover:bg-amber-700 text-white',
    Icon: AlertTriangle,
    showIrreversibleWarning: false
  },
  info: {
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    confirmBtnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    Icon: Info,
    showIrreversibleWarning: false
  },
  success: {
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    confirmBtnClass: 'bg-green-600 hover:bg-green-700 text-white',
    Icon: CheckCircle,
    showIrreversibleWarning: false
  },
  question: {
    bgColor: 'bg-[#FAF9F7]',
    borderColor: 'border-[#E7E5E4]',
    iconBg: 'bg-[#D4AF37]/10',
    iconColor: 'text-[#D4AF37]',
    confirmBtnClass: 'bg-[#D4AF37] hover:bg-[#B8960F] text-white',
    Icon: HelpCircle,
    showIrreversibleWarning: false
  }
};

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Conferma azione',
  message = 'Sei sicuro di voler procedere?',
  details = null,
  confirmText = 'Conferma',
  cancelText = 'Annulla',
  variant = 'danger',
  icon: CustomIcon = null,
  loading = false
}) => {
  if (!isOpen) return null;

  const variantStyle = variants[variant] || variants.danger;
  const Icon = CustomIcon || variantStyle.Icon;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className={`relative w-full max-w-md bg-white rounded-2xl shadow-2xl border ${variantStyle.borderColor} overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con gradiente sottile */}
        <div className={`${variantStyle.bgColor} px-6 py-4 border-b ${variantStyle.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${variantStyle.iconBg}`}>
                <Icon className={variantStyle.iconColor} size={24} />
              </div>
              <h3 className="text-lg font-heading font-semibold text-[#1C1917]">
                {title}
              </h3>
            </div>
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-black/5 transition-colors"
              aria-label="Chiudi"
            >
              <X size={20} className="text-[#78716C]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-[#44403C] leading-relaxed">
            {message}
          </p>
          
          {details && (
            <div className={`mt-4 p-3 rounded-lg ${variantStyle.bgColor} border ${variantStyle.borderColor}`}>
              <p className="text-sm font-medium text-[#1C1917]">
                {details}
              </p>
            </div>
          )}

          {variant === 'danger' && (
            <p className="mt-4 text-sm text-red-600 font-semibold flex items-center gap-2">
              <AlertTriangle size={16} />
              ⚠️ Questa azione è IRREVERSIBILE e i dati verranno eliminati definitivamente dal database.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#FAF9F7] border-t border-[#E7E5E4] flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="min-w-[100px]"
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className={`min-w-[100px] ${variantStyle.confirmBtnClass}`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Attendere...
              </span>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
